import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import { coreAPI, UserSitePermission } from '../lib/api';
import { useAuth } from './AuthContext';

interface UserPermissionsContextType {
  sitePermissions: UserSitePermission[];
  isLoading: boolean;
  error: string | null;
  refreshPermissions: () => Promise<void>;
  // Module access checks - returns true if user has access to module at ANY linked site
  hasModuleAccess: (module: 'purchasing' | 'quality' | 'finance') => boolean;
  // Check if user has specific capability at ANY linked site
  hasCapability: (capability: 'admin' | 'approve' | 'readOnly') => boolean;
  // Get linked sites
  getLinkedSites: () => UserSitePermission[];
  // CR-4203: Check if user has write purchasing access for a specific site
  hasPurchasingWriteAccess: (siteId: number | undefined) => boolean;
}

const UserPermissionsContext = createContext<UserPermissionsContextType | undefined>(undefined);

/**
 * Provides site-level permission data for the authenticated user.
 * Fetches permissions on mount and exposes helpers for module access and capability checks.
 */
export const UserPermissionsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [sitePermissions, setSitePermissions] = useState<UserSitePermission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated || !user?.profile_id) {
      setSitePermissions([]);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const response = await coreAPI.getUserSitePermissions(user.profile_id);
      setSitePermissions(response.data);
    } catch (err: unknown) {
      console.error('Failed to fetch user site permissions:', err);
      setError(err instanceof Error ? err.message : 'Failed to load permissions');
      setSitePermissions([]);
    } finally {
      setIsLoading(false);
    }
  }, [isAuthenticated, user?.profile_id]);

  // Fetch permissions when user changes
  useEffect(() => {
    refreshPermissions();
  }, [refreshPermissions]);

  // Check if user has access to a specific module at ANY linked site
  const hasModuleAccess = useCallback((module: 'purchasing' | 'quality' | 'finance'): boolean => {
    // If no permissions loaded yet, default to true (allow access until we know)
    if (sitePermissions.length === 0) return true;

    // Check if user has this module enabled at ANY linked/active site
    const linkedSites = sitePermissions.filter(p => p.is_linked && p.is_active);

    // If no linked sites, allow access (backward compatibility)
    if (linkedSites.length === 0) return true;

    switch (module) {
      case 'purchasing':
        return linkedSites.some(p => p.can_purchasing);
      case 'quality':
        return linkedSites.some(p => p.can_quality);
      case 'finance':
        return linkedSites.some(p => p.can_finance);
      default:
        return true;
    }
  }, [sitePermissions]);

  // Check if user has a specific capability at ANY linked site
  const hasCapability = useCallback((capability: 'admin' | 'approve' | 'readOnly'): boolean => {
    const linkedSites = sitePermissions.filter(p => p.is_linked && p.is_active);

    if (linkedSites.length === 0) return false;

    switch (capability) {
      case 'admin':
        return linkedSites.some(p => p.is_admin);
      case 'approve':
        return linkedSites.some(p => p.can_approve);
      case 'readOnly':
        return linkedSites.every(p => p.is_read_only); // All sites must be read-only
      default:
        return false;
    }
  }, [sitePermissions]);

  // Get all linked sites
  const getLinkedSites = useCallback((): UserSitePermission[] => {
    return sitePermissions.filter(p => p.is_linked && p.is_active);
  }, [sitePermissions]);

  // CR-4203: Check if user has write purchasing access for a specific delivery site
  const hasPurchasingWriteAccess = useCallback((siteId: number | undefined): boolean => {
    // No site ID — allow (backward compatibility)
    if (!siteId) return true;

    const linkedSites = sitePermissions.filter(p => p.is_linked && p.is_active);

    // No site permissions configured — allow (backward compatibility)
    if (linkedSites.length === 0) return true;

    const perm = linkedSites.find(p => p.site === siteId);
    if (!perm) return false;

    return perm.can_purchasing && !perm.is_read_only;
  }, [sitePermissions]);

  return (
    <UserPermissionsContext.Provider
      value={{
        sitePermissions,
        isLoading,
        error,
        refreshPermissions,
        hasModuleAccess,
        hasCapability,
        getLinkedSites,
        hasPurchasingWriteAccess,
      }}
    >
      {children}
    </UserPermissionsContext.Provider>
  );
};

/**
 * Accesses the current user's site-level permissions.
 * Must be used within a {@link UserPermissionsProvider}.
 *
 * @returns Permission state with module access checks (purchasing, quality, finance) and capability helpers
 */
export const useUserPermissions = (): UserPermissionsContextType => {
  const context = useContext(UserPermissionsContext);
  if (!context) {
    throw new Error('useUserPermissions must be used within a UserPermissionsProvider');
  }
  return context;
};
